import { memo, useMemo } from 'react';
import { Empty, Skeleton } from 'antd';
import { Panel } from './Panel';
import { useWidgetData, groupByCategory, defaultPalette, currencyFmt } from './shared';
import type { DashboardWidget } from '../../types/panelConfig';

interface Props {
  widget: Extract<DashboardWidget, { type: 'chart-hbar' }>;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

// Horizontal bars: one row per category (label · value · % + filled track).
// Cleanest read for a small set of categories (e.g. payment mix POS/Nakit).
function HBarWidgetImpl({ widget, sourceId, parameters }: Props) {
  const { data, isLoading } = useWidgetData(sourceId, `chart-hbar-${widget.reportCode}`, parameters);
  const { categoryField, valueField, aggregation = 'sum', topN, title, subtitle, color } = widget.config;
  const palette = Array.isArray(color) && color.length > 0 ? color : defaultPalette;

  const { rows, total } = useMemo(() => {
    const grouped = groupByCategory(data ?? [], categoryField, valueField, aggregation).sort(
      (a, b) => b.value - a.value,
    );
    const sliced = topN ? grouped.slice(0, topN) : grouped;
    return { rows: sliced, total: sliced.reduce((s, r) => s + r.value, 0) };
  }, [data, categoryField, valueField, aggregation, topN]);

  return (
    <div data-testid="chart-hbar-widget">
      <Panel title={title} subtitle={subtitle}>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : rows.length === 0 ? (
          <Empty description="Veri yok" style={{ padding: 40 }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 6 }}>
            {rows.map((r, i) => {
              const pct = total > 0 ? Math.round((r.value / total) * 100) : 0;
              const fill = palette[i % palette.length];
              return (
                <div key={r.name}>
                  <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.name}</span>
                    <span className="tnum" style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                      {currencyFmt.format(r.value)}
                    </span>
                    <span className="tnum" style={{ marginLeft: 10, fontSize: 12.5, color: 'var(--color-text-muted)', minWidth: 38, textAlign: 'right' }}>
                      %{pct}
                    </span>
                  </div>
                  <div style={{ height: 8, borderRadius: 5, background: 'var(--color-border-light, #eef1f5)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 5, background: fill, transition: 'width .3s ease' }} />
                  </div>
                </div>
              );
            })}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 6,
                paddingTop: 14,
                borderTop: '1px solid var(--color-border)',
              }}
            >
              <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>Toplam</span>
              <span className="tnum" style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>
                {currencyFmt.format(total)}
              </span>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}

export const HBarWidget = memo(HBarWidgetImpl);
